import { MenuDataProps } from '@/components/AntdForm/antd-components.def.ts';
import { Role } from '@/features/auth/auth.def.ts';
import {
  BarChartOutlined,
  CloudDownloadOutlined,
  DatabaseOutlined,
  DesktopOutlined,
  DiffOutlined,
  DollarOutlined,
  FileExcelOutlined,
  FundOutlined,
  HistoryOutlined,
  MessageOutlined,
  PieChartOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  TeamOutlined,
} from '@ant-design/icons';

export const routes = {
  login: '/login',
  logout: '/logout',
  customerRelationshipManagement: '/customer-relationship-management',
  analytics: '/analytics',
  analyticsInvestors: '/analytics/investors',
  analyticsNavBreakdown: '/analytics/nav-breakdown',
  analyticsFunds: '/analytics/funds',
  // Compatibility entry points. New internal links use the canonical paths above.
  fundDashboard: '/fund-management/dashboard',
  fundDetail: '/fund-management/fund',
  fundManagement: '/fund-management',
  investorManagement: '/investor-management',
  investorDashboard: '/investor-management/dashboard',
  investorInteractions: '/investor-management/interactions',
  snowflakeImport: '/investor-management/snowflake-import',
  investorReconciliation: '/investor-management/reconciliation',
  investorProfile: '/investor-management/investor',
  auditLogs: '/audit-logs',
  dataMaskingRuns: '/operations/data-masking',
  referenceData: '/reference-data',
  reports: '/reports',
  navBreakdownReport: '/reports/nav-breakdown',
} as const;

export type AnalyticsTabKey = 'investors' | 'nav-breakdown' | 'funds';

/**
 * Label for a "back" button that returns to the page at this route. Keys are route prefixes;
 * the longest matching prefix wins, so parameterised routes (`/fund-management/fund/:id`) match.
 */
export const routeBackLabels: Readonly<Record<string, string>> = {
  [routes.analyticsInvestors]: 'Back to Investors',
  [routes.analyticsNavBreakdown]: 'Back to NAV Breakdown',
  [routes.analyticsFunds]: 'Back to Funds',
  [routes.investorDashboard]: 'Back to Investor Dashboard',
  [routes.investorInteractions]: 'Back to Investor Interactions',
  [routes.investorReconciliation]: 'Back to Reconciliation',
  [routes.fundDashboard]: 'Back to Fund Dashboard',
  [routes.fundDetail]: 'Back to Fund Detail',
  [routes.navBreakdownReport]: 'Back to NAV Breakdown',
};

/** The Fund Dashboard, Fund Detail and NAV Breakdown routes' permission (`pages/fund-management`, `pages/reports`). */
const READ_INVESTOR_PROFILE = {
  moduleName: 'investor-management',
  functionName: 'read-investor-profile',
} as const;

export const navMenuData: MenuDataProps[] = [
  {
    key: 'customerRelationshipManagement',
    url: routes.customerRelationshipManagement,
    label: 'CRM',
    icon: <DesktopOutlined />,
    children: [
      {
        key: 'apiQuery',
        url: `${routes.customerRelationshipManagement}/api-example`,
        label: 'API Example',
      },
    ],
    allowRoles: ['ir-developer'],
    requiredPermissions: [{ moduleName: 'assume-identity', functionName: 'assume-user' }],
  },
  // Review L0-01/L0-02, S-02, S-04. Permissions here are UX gates only; the route guards and the
  // controllers' attributes are the security boundary. Fund Dashboard is no longer developer-only.
  {
    key: 'analytics',
    label: 'Analytics',
    description: 'Holdings, NAV and returns, filtered by fund, month and investor attributes.',
    icon: <BarChartOutlined />,
    children: [
      {
        icon: <TeamOutlined />,
        key: 'investorDashboard',
        url: routes.analyticsInvestors,
        label: 'Investor Dashboard',
        description: 'Every investor with commitment, NAV, ownership and returns per fund.',
      },
      {
        icon: <FundOutlined />,
        key: 'fundDashboard',
        url: routes.analyticsFunds,
        label: 'Fund Dashboard',
        description: 'Each fund’s net subscribed, NAV, investors and returns.',
        requiredPermissions: [READ_INVESTOR_PROFILE],
      },
      {
        icon: <PieChartOutlined />,
        key: 'navBreakdownReport',
        url: routes.analyticsNavBreakdown,
        label: 'NAV Breakdown',
        description: 'NAV split by fund, share class and country.',
        requiredPermissions: [READ_INVESTOR_PROFILE],
      },
    ],
  },
  {
    key: 'auditLogs',
    url: routes.auditLogs,
    label: 'Audit Log',
    description: 'Raw change history across the portal.',
    icon: <HistoryOutlined />,
    allowRoles: ['ir-developer'],
  },
  {
    key: 'dataMaskingRuns',
    url: routes.dataMaskingRuns,
    label: 'Data Masking',
    description: 'Run and monitor data masking.',
    icon: <SafetyCertificateOutlined />,
    allowRoles: [Role.Developer],
    requiredPermissions: [
      { moduleName: 'investor-management', functionName: 'read-data-masking-run-status' },
    ],
  },
  {
    key: 'snowflakeImport',
    url: routes.snowflakeImport,
    label: 'Snowflake Import',
    description: 'Import investment records from Snowflake.',
    icon: <CloudDownloadOutlined />,
    allowRoles: [Role.Developer],
  },
  {
    key: 'referenceData',
    url: routes.referenceData,
    label: 'Fund & Share Class Management',
    description: 'Funds, share classes and their reference data.',
    icon: <SettingOutlined />,
  },
  {
    key: 'investorManagement',
    // url: routes.investorManagement,
    label: 'Investor Management',
    description: 'Interactions, reconciliation and investor uploads.',
    icon: <DatabaseOutlined />,
    children: [
      {
        icon: <MessageOutlined />,
        key: 'investorInteractions',
        url: routes.investorInteractions,
        label: 'Investor Interactions',
        description: 'Meetings, calls and notes with investors.',
      },
      {
        icon: <DiffOutlined />,
        key: 'investorReconciliation',
        url: routes.investorReconciliation,
        label: 'Investor Reconciliation',
        description: 'Review and apply reconciliation plans.',
        allowRoles: [Role.Developer, Role.InvestorRelations],
        requiredPermissions: [
          { moduleName: 'investor-management', functionName: 'review-reconciliation-plan' },
        ],
      },
      {
        icon: <FileExcelOutlined />,
        key: 'uploadInvestorWorkbook',
        url: `${routes.investorManagement}/upload-investor-workbook`,
        label: 'Upload Investor Workbook',
        description: 'Update investor details from a workbook.',
      },
      {
        icon: <DollarOutlined />,
        key: 'uploadInvestmentRecords',
        url: `${routes.investorManagement}/upload-investment-records`,
        label: 'Upload Investment Records',
        description: 'Load investment records from a file.',
      },
    ],
  },
];
